import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Link as RouterLink } from 'react-router-dom';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import {
  createUser,
  deleteUser,
  getUserNominations,
  grantUserNomination,
  listNominations,
  listUsers,
  sendLoginLink,
  revokeUserNomination,
  setUserPassword,
  setUserStatus,
  updateUser,
  type Nomination,
  type User,
  type UserNomination,
  type UserRole,
  type UserStatus,
} from '../lib/api';
import { wafTableHeadSx } from '../theme';
import { WafDataGrid } from '../components/WafDataGrid';
import { LoadingScreen } from '../components/LoadingScreen';
import { PasswordRequirements, passwordMeetsAll } from '../components/PasswordRequirements';
import { roleChipColor, statusChipColor, roleDisplayName, statusDisplayName } from '../lib/roles';

type Mode = 'create' | 'edit';

interface UserFormState {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

const EMPTY_FORM: UserFormState = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'User',
};

const ROLE_OPTIONS: UserRole[] = ['Admin', 'User', 'Entrant', 'PR'];

export function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  // Seed the search from a ?q= param so other pages (e.g. Change Requests) can
  // deep-link straight to a specific architect's record here.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [filterRole, setFilterRole] = useState<UserRole | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [dialog, setDialog] = useState<{ mode: Mode; target?: User } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<User | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ user: User; next: UserStatus } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  // Per-row pending set keyed on user id. Resend regenerates the invite
  // token server-side, so a double-click would invalidate the first email
  // — disable while in flight rather than rely on user discipline.
  const [pending, setPending] = useState<Set<string>>(new Set());

  const markPending = (id: string) =>
    setPending((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const clearPending = (id: string) =>
    setPending((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const isPending = (id: string) => pending.has(id);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      // u.role arrives lower-cased from the API; normalise before comparing to
      // the PascalCase filter value, else filtering by role matches nothing.
      if (filterRole && normalizeRole(u.role) !== filterRole) return false;
      if (!q) return true;
      return u.email.toLowerCase().includes(q) || u.display_name.toLowerCase().includes(q);
    });
  }, [users, query, filterRole]);

  // Reset to the first page whenever the filter/search changes the result set.
  useEffect(() => {
    setPage(0);
  }, [query, filterRole]);

  const paged = useMemo(
    () => visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visible, page, rowsPerPage]
  );

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listUsers();
      setUsers(rows);
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  const onCreated = (user: User, invitationSent: boolean) => {
    setUsers((prev) => [...prev, user].sort((a, b) => a.email.localeCompare(b.email)));
    setSnack(invitationSent ? `Invite sent to ${user.email}` : `User created; email delivery failed — try resending.`);
    setDialog(null);
  };

  const onUpdated = (user: User) => {
    setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
    setSnack(`Updated ${user.email}`);
    setDialog(null);
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    if (isPending(deleteTarget.id)) return;
    const target = deleteTarget;
    markPending(target.id);
    try {
      await deleteUser(target.id);
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
      setSnack(`Deleted ${target.email}`);
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Delete failed.');
    } finally {
      clearPending(target.id);
      setDeleteTarget(null);
    }
  };

  const onSendLink = async (user: User) => {
    if (isPending(user.id)) return;
    markPending(user.id);
    try {
      const { kind, invitation_sent } = await sendLoginLink(user.id);
      const what = kind === 'invite' ? 'Account-setup link' : 'Password-reset link';
      if (invitation_sent) {
        setSnack(`${what} sent to ${user.email}`);
      } else {
        // Token was regenerated server-side, but the email never went out —
        // surface as an error so the admin knows the user has no working link.
        setError(`${what} generated but email delivery failed for ${user.email}.`);
      }
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Send failed.');
    } finally {
      clearPending(user.id);
    }
  };

  const replaceUser = (next: User) => {
    setUsers((prev) => prev.map((u) => (u.id === next.id ? next : u)));
  };

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((u) => selected.has(u.id));
  const someVisibleSelected = visible.some((u) => selected.has(u.id));
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const u of visible) next.delete(u.id);
      } else {
        for (const u of visible) next.add(u.id);
      }
      return next;
    });

  const onConfirmStatus = async () => {
    if (!statusTarget) return;
    const { user, next } = statusTarget;
    if (isPending(user.id)) return;
    markPending(user.id);
    try {
      const updated = await setUserStatus(user.id, next);
      replaceUser(updated);
      setSnack(
        next === 'Suspended'
          ? `Suspended ${updated.email}`
          : `Reactivated ${updated.email}`
      );
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Status change failed.');
    } finally {
      clearPending(user.id);
      setStatusTarget(null);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight={600}>
          Users
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ mode: 'create' })}>
            Add user
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Search"
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Role"
            size="small"
            select
            value={filterRole}
            onChange={(e) => setFilterRole((e.target.value as UserRole | '') ?? '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {ROLE_OPTIONS.map((r) => (
              <MenuItem key={r} value={r}>
                {roleDisplayName(r)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper>
        {loading ? (
          <LoadingScreen />
        ) : (
          <>
          <TableContainer sx={{ backgroundColor: 'background.paper', overflowX: 'auto' }}>
            <Table sx={wafTableHeadSx}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      inputProps={{ 'aria-label': 'select all visible users' }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last login</TableCell>
                  <TableCell>Confirmed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((u) => {
                    const busy = isPending(u.id);
                    const isSelected = selected.has(u.id);
                    return (
                      <TableRow
                        key={u.id}
                        hover
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setDrawerUser(u)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleRow(u.id)}
                            inputProps={{ 'aria-label': `select ${u.email}` }}
                          />
                        </TableCell>
                        <TableCell>{u.display_name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.phone || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={roleChipColor(u.role)} label={roleDisplayName(u.role)} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={statusChipColor(u.status)} label={statusDisplayName(u.status)} />
                        </TableCell>
                        <TableCell>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</TableCell>
                        <TableCell>
                          {u.details_confirmed
                            ? <Chip size="small" color="success" label="Yes" />
                            : <Chip size="small" label="No" />}
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {u.status !== 'Suspended' && (
                              <Tooltip title={u.status === 'Pending' ? 'Send account-setup link' : 'Send password-reset link'}>
                                <span>
                                  <IconButton size="small" onClick={() => onSendLink(u)} disabled={busy}>
                                    <EmailIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={u.status === 'Pending' ? 'Set password & activate' : 'Set password'}>
                              <span>
                                <IconButton size="small" onClick={() => setPasswordTarget(u)} disabled={busy}>
                                  <KeyIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {u.status === 'Suspended' ? (
                              <Tooltip title="Reactivate">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => setStatusTarget({ user: u, next: 'Active' })}
                                    disabled={busy}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Suspend">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => setStatusTarget({ user: u, next: 'Suspended' })}
                                    disabled={busy}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => setDialog({ mode: 'edit', target: u })}
                                  disabled={busy}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteTarget(u)}
                                  color="error"
                                  disabled={busy}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
          </>
        )}
      </Paper>

      <UserDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        target={dialog?.target}
        onClose={() => setDialog(null)}
        onCreated={onCreated}
        onUpdated={onUpdated}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete <strong>{deleteTarget?.email}</strong> in Zitadel and mark the local record as deleted.
            The user will be unable to sign in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={onDelete}
            disabled={!!deleteTarget && isPending(deleteTarget.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <SetPasswordDialog
        target={passwordTarget}
        onClose={() => setPasswordTarget(null)}
        onSaved={(updated) => {
          replaceUser(updated);
          setSnack(`Password set for ${updated.email}`);
          setPasswordTarget(null);
        }}
        onError={(msg) => setError(msg)}
      />

      <Dialog open={!!statusTarget} onClose={() => setStatusTarget(null)}>
        <DialogTitle>
          {statusTarget?.next === 'Suspended' ? 'Suspend user?' : 'Reactivate user?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {statusTarget?.next === 'Suspended' ? (
              <>
                <strong>{statusTarget?.user.email}</strong> will be locked out of Zitadel. Existing access tokens
                stay valid until they expire (typically under an hour).
              </>
            ) : (
              <>
                <strong>{statusTarget?.user.email}</strong> will be re-enabled in Zitadel and can sign in again.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={statusTarget?.next === 'Suspended' ? 'warning' : 'success'}
            onClick={onConfirmStatus}
            disabled={!!statusTarget && isPending(statusTarget.user.id)}
          >
            {statusTarget?.next === 'Suspended' ? 'Suspend' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <UserAccessModal
        user={drawerUser}
        onClose={() => setDrawerUser(null)}
        onError={(msg) => setError(msg)}
        onMessage={(msg) => setSnack(msg)}
      />

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} message={snack ?? ''} />
    </Stack>
  );
}

interface UserAccessModalProps {
  user: User | null;
  onClose: () => void;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}

interface AllNomRow extends Nomination {
  /** DataGrid requires a unique `id`. nomination_uuid is the natural key. */
  id: string;
}

/**
 *     Replaces the older right-side drawer with a full-size modal so the
 *     operator can browse the entries table directly when granting access
 *     instead of having to memorise/copy a 15-char nomination_code. Top
 *     half = the user's existing grants with revoke; bottom half = a
 *     searchable table of every nomination with a per-row Link button
 *     (greyed and labelled "Linked" once already granted).
 */
function UserAccessModal({ user, onClose, onError, onMessage }: UserAccessModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [grants, setGrants] = useState<UserNomination[]>([]);
  const [allNoms, setAllNoms] = useState<AllNomRow[]>([]);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [revokingUuid, setRevokingUuid] = useState<string | null>(null);
  const [grantingUuid, setGrantingUuid] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoadingGrants(true);
    setLoadingAll(true);
    setSearch('');
    getUserNominations(user.id)
      .then((r) => {
        if (!cancelled) setGrants(r);
      })
      .catch((err) => {
        if (!cancelled) onError(extractErrorMessage(err) ?? 'Failed to load entries.');
      })
      .finally(() => {
        if (!cancelled) setLoadingGrants(false);
      });
    listNominations()
      .then((r) => {
        if (!cancelled) setAllNoms(r.map((n) => ({ ...n, id: n.nomination_uuid })));
      })
      .catch((err) => {
        if (!cancelled) onError(extractErrorMessage(err) ?? 'Failed to load entries.');
      })
      .finally(() => {
        if (!cancelled) setLoadingAll(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, onError]);

  const grantedSet = useMemo(
    () => new Set(grants.map((g) => g.nomination_uuid)),
    [grants]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allNoms;
    return allNoms.filter((n) =>
      (n.nomination_code ?? '').toLowerCase().includes(q) ||
      (n.project_title ?? '').toLowerCase().includes(q) ||
      (n.architect_company_name ?? '').toLowerCase().includes(q) ||
      (n.project_country ?? '').toLowerCase().includes(q) ||
      (n.category_name ?? '').toLowerCase().includes(q)
    );
  }, [allNoms, search]);

  const onRevoke = async (uuid: string) => {
    if (!user) return;
    setRevokingUuid(uuid);
    try {
      await revokeUserNomination(user.id, uuid);
      setGrants((prev) => prev.filter((r) => r.nomination_uuid !== uuid));
      onMessage('Access revoked');
    } catch (err) {
      onError(extractErrorMessage(err) ?? 'Revoke failed.');
    } finally {
      setRevokingUuid(null);
    }
  };

  const onLink = async (row: AllNomRow) => {
    if (!user) return;
    setGrantingUuid(row.nomination_uuid);
    try {
      const result = await grantUserNomination(user.id, row.nomination_code);
      if (!result.already_granted) {
        setGrants((prev) => [
          ...prev,
          {
            nomination_uuid: result.nomination_uuid,
            nomination_code: result.nomination_code,
            project_title: result.project_title,
            // The DataGrid row already has these — copy across so the
            // top-of-modal list mirrors what the operator sees in the
            // table without needing a refetch.
            category_name: row.category_name ?? null,
            project_country: row.project_country ?? null,
            architect_company_name: row.architect_company_name ?? null,
          },
        ]);
        onMessage(`Granted access to ${result.nomination_code}`);
      }
    } catch (err) {
      onError(extractErrorMessage(err) ?? 'Grant failed.');
    } finally {
      setGrantingUuid(null);
    }
  };

  const columns: GridColDef<AllNomRow>[] = useMemo(
    () => [
      { field: 'nomination_code', headerName: 'Project ID', width: 150 },
      { field: 'project_title', headerName: 'Title', flex: 1.5, minWidth: 220 },
      { field: 'architect_company_name', headerName: 'Architect', flex: 1, minWidth: 180 },
      { field: 'project_country', headerName: 'Country', width: 130 },
      { field: 'category_name', headerName: 'Category', flex: 1, minWidth: 160 },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (p: GridRenderCellParams<AllNomRow>) => {
          const linked = grantedSet.has(p.row.nomination_uuid);
          const busy = grantingUuid === p.row.nomination_uuid;
          if (linked) {
            return <Chip size="small" label="Linked" color="success" variant="outlined" />;
          }
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddLinkIcon fontSize="small" />}
              onClick={() => onLink(p.row)}
              disabled={busy}
            >
              {busy ? '…' : 'Link'}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grantedSet, grantingUuid]
  );

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      {user && (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            <Box>
              <Typography variant="h6" component="div">
                Manage access — {user.display_name || user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email} · {roleDisplayName(user.role)}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {/* Top: existing grants */}
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Entries this user can edit ({grants.length})
              </Typography>
              {loadingGrants ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : grants.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No entries granted yet.
                </Typography>
              ) : (
                // Auto-fill grid: cards reflow to 1, 2, or 3 across as the
                // modal width allows, instead of a single tall column.
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 1,
                  }}
                >
                  {grants.map((g) => (
                    <Paper key={g.nomination_uuid} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <MuiLink
                            component={RouterLink}
                            to={`/entry/${g.nomination_uuid}`}
                            underline="hover"
                            sx={{
                              fontWeight: 500,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={g.project_title || g.nomination_code}
                          >
                            {g.project_title || g.nomination_code}
                          </MuiLink>
                          <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={[g.nomination_code, g.category_name, g.architect_company_name]
                              .filter(Boolean)
                              .join(' · ')}
                          >
                            {g.nomination_code}
                            {g.category_name ? ` · ${g.category_name}` : ''}
                            {g.architect_company_name ? ` · ${g.architect_company_name}` : ''}
                          </Typography>
                        </Box>
                        <Tooltip title="Remove access">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onRevoke(g.nomination_uuid)}
                              disabled={revokingUuid === g.nomination_uuid}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
            <Divider />

            {/* Bottom: searchable entries table */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2">
                  Grant access to an entry
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by project ID, title, architect, country, category…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 380 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Box sx={{ height: 480 }}>
                <WafDataGrid<AllNomRow>
                  rows={filtered}
                  columns={columns}
                  loading={loadingAll}
                  initialState={{
                    sorting: { sortModel: [{ field: 'nomination_code', sort: 'asc' }] },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Done</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

interface SetPasswordDialogProps {
  target: User | null;
  onClose: () => void;
  onSaved: (user: User) => void;
  onError: (message: string) => void;
}

function SetPasswordDialog({ target, onClose, onSaved, onError }: SetPasswordDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setPassword('');
    setConfirm('');
    setLocalError(null);
  }, [target]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!target) return;
    if (!passwordMeetsAll(password)) {
      setLocalError('Password must meet all the requirements shown below.');
      return;
    }
    if (password !== confirm) {
      setLocalError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      const updated = await setUserPassword(target.id, password);
      onSaved(updated);
    } catch (err) {
      const msg = extractErrorMessage(err) ?? 'Set password failed.';
      // Surface inline so the dialog stays open for retries.
      setLocalError(msg);
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <form onSubmit={submit}>
        <DialogTitle>Set password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {target?.status === 'Pending' && (
              <Alert severity="info">
                {target.email} hasn't accepted their invite. Setting a password here will activate their account
                and invalidate the welcome link.
              </Alert>
            )}
            {localError && <Alert severity="error">{localError}</Alert>}
            <TextField
              label="New password"
              type="password"
              fullWidth
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
            />
            <PasswordRequirements password={password} />
            <TextField
              label="Confirm password"
              type="password"
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
            />
            <Typography variant="caption" color="text.secondary">
              Minimum 8 characters. The user will need to use this password on their next sign-in.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !password}>
            {submitting ? 'Saving…' : 'Set password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

interface UserDialogProps {
  open: boolean;
  mode: Mode;
  target?: User;
  onClose: () => void;
  onCreated: (user: User, invitationSent: boolean) => void;
  onUpdated: (user: User) => void;
}

/**
 * Split a display_name into a (first, last) pair. We split on the first
 * space, so a name like "María del Carmen Sánchez" becomes
 * { first: "María", last: "del Carmen Sánchez" }. That's not strictly
 * accurate as a name model, but it round-trips losslessly: if the admin
 * doesn't edit the fields, joining first + last reconstructs the
 * original string verbatim.
 */
function splitDisplayName(name: string): { first: string; last: string } {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return { first: '', last: '' };
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1).trim() };
}

/**
 * The backend serializes UserRole values as lowercase (snake_case JSON
 * naming policy). The select component has options capitalised
 * ('Admin', 'User', 'Entrant'), so without normalising we'd get a blank
 * dropdown when editing an existing user.
 */
function normalizeRole(role: string | undefined | null): UserRole {
  const lower = (role ?? '').toLowerCase();
  if (lower === 'admin') return 'Admin';
  if (lower === 'entrant') return 'Entrant';
  if (lower === 'pr') return 'PR';
  return 'User';
}

function UserDialog({ open, mode, target, onClose, onCreated, onUpdated }: UserDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && target) {
      const { first, last } = splitDisplayName(target.display_name);
      setForm({
        email: target.email,
        firstName: first,
        lastName: last,
        role: normalizeRole(target.role),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [open, mode, target]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const { user, invitation_sent } = await createUser({
          email: form.email.trim(),
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          role: form.role,
        });
        onCreated(user, invitation_sent);
      } else if (target) {
        // Always send all editable fields. The backend's UpdateAsync is a
        // no-op when the incoming value matches the stored value, so
        // sending the unchanged values is harmless and keeps the frontend
        // simple. Email is the only field we still gate on a diff because
        // an email change kicks off Zitadel re-verification.
        const updated = await updateUser(target.id, {
          email: form.email !== target.email ? form.email.trim() : undefined,
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          role: form.role,
        });
        onUpdated(updated);
      }
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <form onSubmit={submit}>
        <DialogTitle>{mode === 'create' ? 'Add user' : 'Edit user'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={submitting}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                fullWidth
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                disabled={submitting}
              />
              <TextField
                label="Last name"
                fullWidth
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                disabled={submitting}
              />
            </Stack>
            <TextField
              label="Role"
              select
              fullWidth
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
              disabled={submitting}
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {roleDisplayName(r)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !form.email}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Create & invite' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function extractErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const maybe = err as { response?: { data?: { message?: string; error?: string } } };
  return maybe.response?.data?.message ?? maybe.response?.data?.error ?? null;
}
